import { useCallback } from 'react';
import { useSelector } from 'react-redux';

import { selectUserToken } from '../redux/features/user/userSelectors';
import {
  useCreateEventLogMutation,
  EVENT_LOG_TYPES,
} from '../redux/services/eventLog/eventLogApi';

/**
 * Hook returning a callback that opens WhatsApp Web/app for a phone number and
 * fires a `user.whatsapp_sent` EventLog row on the backend (best-effort —
 * never blocks the redirect; silent failure on network error or missing token).
 *
 * Backend endpoint: `POST /api/event-logs` — see `EventLogController::store()`
 * and `StoreEventLogRequest` in Machingapp-back.
 *
 * @returns {(phone: string, context?: WhatsAppContext) => void}
 *
 * @typedef {Object} WhatsAppContext
 * @property {('realty'|'client'|'client_request'|'reminder')} subject_type
 *   What the WhatsApp message is about. Backend enum — `EVENT_LOG_SUBJECTS`
 *   constants in `eventLogApi.js`. Required for the log to be written.
 *   If omitted the log is skipped, WhatsApp still opens.
 * @property {number} subject_id
 *   ID of the realty / client / client_request / reminder the message
 *   relates to. Required for the log to be written.
 * @property {Object} [payload]
 *   Optional extra structured data merged into the EventLog `payload` JSON
 *   column. The hook always adds `{ phone }`; anything passed here is merged
 *   alongside (e.g. `contact_id`, `client_id`, `source: 'CardRealty'`).
 *   Total encoded JSON capped at 64 KB by backend (`payload_max_size`).
 *
 * @example
 *   const sendWhatsApp = useWhatsApp();
 *   sendWhatsApp(contact.phone, {
 *     subject_type: EVENT_LOG_SUBJECTS.REALTY,
 *     subject_id: realty.id,
 *     payload: { contact_id: contact.id },
 *   });
 */
export const useWhatsApp = () => {
  const token = useSelector(selectUserToken);
  const [createEventLog] = useCreateEventLogMutation();

  return useCallback(
    (phone, context) => {
      if (!phone) return;

      if (token && context?.subject_type && context?.subject_id) {
        createEventLog({
          token,
          data: {
            event_type: EVENT_LOG_TYPES.USER_WHATSAPP_SENT,
            subject_type: context.subject_type,
            subject_id: context.subject_id,
            payload: { phone, ...(context.payload || {}) },
          },
        }).catch(() => {});
      }

      const formattedPhone = phone.replace(/[+\-\s]/g, '');
      window.open(
        `https://api.whatsapp.com/send/?phone=${formattedPhone}&text&type=phone_number&app_absent=0`
      );
    },
    [token, createEventLog]
  );
};
